import { MAX_ATTACHMENT_SIZE } from '../utils/attachmentLimits';
import * as Yup from 'yup';

/**
 * Validation schema for Medical Background form.
 * Uses Yup for form validation.
 * @type {Yup.ObjectSchema}
 */

export const medicalBackgroundValidationSchema = Yup.object().shape({
  isTherapyDone: Yup.string(),
    // .required('This is required'), // Made optional

  childTherapyStatus: Yup.array()
    .when('isTherapyDone', (isTherapyDone, schema) => {
      if (isTherapyDone[0] == 'yes') {
        return schema.of(
          Yup.object().shape({
            name: Yup.string().required('This is required'),
            duration: Yup.string().required('This is required'),
            goal: Yup.string().required('This is required'),
          })
        ).required('At least one child therapy status is required');
      }
      return schema;
    }),

  hasOtherTherapist: Yup.string(),
    // .required('This is required'), // Made optional
  therapistRecommendation: Yup.string()
    .when('hasOtherTherapist', (hasOtherTherapist, schema) => {
      return hasOtherTherapist == 'yes'
        ? schema.required('This is required')
        : schema;
    }),

  // Image - Required field
  signature: Yup.mixed()
    .required('Signature is required')
    .test(
      "fileSize",
      "The file you're trying to upload is too large. Please make sure it's 10MB or smaller.",
      value => {
        // Allow if value is string (already uploaded file path)
        if (typeof value === 'string') return true;
        // Check file size for new uploads
        return value && value.size <= MAX_ATTACHMENT_SIZE; // 10MB
      }
    ).test(
      'fileType',
      'Invalid file format. Only image files (.jpg, .jpeg, .png, .heif, .heic, .hei) are allowed.',
      (value) => {
        // Allow if value is string (already uploaded file path from draft/restore)
        if (typeof value === 'string') return true;
        
        if (!value) return false;
        
        // Validate by file extension (more reliable than MIME type for HEIF/HEIC)
        const fileName = value.name ? value.name.toLowerCase() : '';
        const validExtensions = ['.jpg', '.jpeg', '.png', '.heif', '.heic', '.hei'];
        const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));
        
        // Also check MIME type as backup validation
        const validMimeTypes = [
          'image/jpeg', 
          'image/png', 
          'image/heif', 
          'image/heic',
          'image/heif-sequence',
          'image/heic-sequence',
          'image/x-heic'
        ];
        const hasValidMimeType = value.type && validMimeTypes.includes(value.type);
        
        // Accept if either extension or MIME type is valid (HEIC MIME types vary by browser)
        return hasValidExtension || hasValidMimeType;
      }
    ),

  // Child Development - Made optional
  childPlaymatesAge: Yup.string(),
    // .required('This field is required'), // Made optional
  
  childSeparationIssues: Yup.array(),
    // .min(1, 'At least one child separation issue must be selected') // Made optional
    // .required('This field is required'), // Made optional

  specificSeparationIssue: Yup.string()
    .when('childSeparationIssues', (value, schema) => {
      return value && value[0] && value[0].includes('At iba pa')
        ? schema.required('This is required')
        : schema;
    }),

  childLanguagesUse: Yup.array(),
    // .min(1, 'At least one child language use must be selected') // Made optional
    // .required('This is required'), // Made optional

  specificChildLanguagesUse: Yup.string()
    .when('childLanguagesUse', (value, schema) => {
      return value && value[0] && value[0].includes('At iba pa')
        ? schema.required('This is required')
        : schema;
    }),

  showTextFieldA: Yup.string(),
  showTextFieldB: Yup.string(),
  showTextFieldC: Yup.string(),
  showTextFieldD: Yup.string(),
  showTextFieldE: Yup.string(),

  childPhysicalChallenges: Yup.array()
    .when('showTextFieldA', (value, schema) => {
      return value[0] === 'true'
        ? schema.min(1, 'At least one child physical challenges must be selected')
          .required('This is required')
        : schema;
    }),

  childModeOfMobility: Yup.array()
    .when('showTextFieldA', (value, schema) => {
      return value[0] === 'true'
        ? schema.min(1, 'At least one child mode of mobility must be selected')
          .required('This is required')
        : schema;
    }),


  childSensesChallenges: Yup.array()
    .when('showTextFieldB', (value, schema) => {
      return value[0] === 'true'
        ? schema.min(1, 'At least one child senses challenges must be selected')
          .required('This is required')
        : schema;
    }),

  childCognitiveChallenges: Yup.array()
    .when('showTextFieldC', (value, schema) => {
      return value[0] === 'true'
        ? schema.min(1, 'At least one child cognitive challenges must be selected')
          .required('This is required')

        : schema;
    }),

  childFollowingInstructions: Yup.string()
    .when('showTextFieldC', (value, schema) => {
      return value[0] === 'true'
        ? schema.required('This is required')
        : schema;
    }),

  childAttitudeChallenges: Yup.array()
    .when('showTextFieldD', (value, schema) => {
      return value[0] === 'true'
        ? schema.min(1, 'At least one child attitude challenges must be selected')
          .required('This is required')
        : schema;
    }),

  childBehaviorChallenges: Yup.array()
    .when('showTextFieldD', (value, schema) => {
      return value[0] === 'true'
        ? schema.min(1, 'At least one child behavior challenges must be selected')
          .required('This is required')
        : schema;
    }),

  childInteractionChallenges: Yup.array()
    .when('showTextFieldE', (value, schema) => {
      return value[0] === 'true'
        ? schema.min(1, 'At least one child interaction challenges must be selected')
          .required('This is required')
        : schema;
    }),

  terms: Yup.string().required('Kinakailangang sumang-ayon bago magpatuloy.'),

  // [AC-FILEUPL0318-E1, S1, S2, E3] Optional requirement file uploads
  // [OWASP:A05] Enforce file count, per-file size, total size, and extension whitelist
  requirementFiles: Yup.array()
    .max(5, 'Maximum of 5 files allowed')
    .test(
      'total-size',
      'Total file size must not exceed 25MB',
      (files) => {
        if (!files || files.length === 0) return true;
        const total = files.reduce((sum, f) => sum + (f?.size || 0), 0);
        return total <= 25 * 1024 * 1024;
      }
    )
    .of(
      Yup.mixed()
        .test('file-size', 'Each file must be smaller than 10MB', (value) => {
          if (!value) return true;
          return value.size <= MAX_ATTACHMENT_SIZE;
        })
        .test(
          'file-type',
          'Invalid file type. Allowed: .jpg, .jpeg, .png, .heif, .heic, .pdf',
          (value) => {
            if (!value) return true;
            const name = value.name ? value.name.toLowerCase() : '';
            const allowed = ['.jpg', '.jpeg', '.png', '.heif', '.heic', '.pdf'];
            return allowed.some((ext) => name.endsWith(ext));
          }
        )
    ),
});